import React, { createContext, useContext, useState, useEffect } from 'react';
import { Quote } from '../types';
import { useSettings } from './SettingsContext';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { calculateQuoteTotal } from '../utils/calculationUtils';
import { supabase } from '../supabaseClient';

interface QuotesContextType {
  quotes: Quote[];
  loading: boolean;
  addQuote: (quote: Omit<Quote, 'id'>) => Promise<Quote>;
  updateQuote: (quote: Quote) => Promise<void>;
  deleteQuote: (id: string) => Promise<void>;
  getQuoteById: (id: string) => Quote | undefined;
  convertQuoteToProject: (quoteId: string) => void;
  fetchQuotes: () => Promise<void>;
  sendQuoteEmail: (quoteId: string, recipientEmail: string, message?: string, pdfBase64?: string, pdfFilename?: string) => Promise<any>;
}

const QuotesContext = createContext<QuotesContextType | undefined>(undefined);

export const useQuotes = () => {
  const context = useContext(QuotesContext);
  if (!context) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }
  return context;
};

export const QuotesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useSettings();
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch quotes from Supabase when authenticated and organization is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchQuotes();
    }
  }, [isAuthenticated, currentOrganization]);

  const fetchQuotes = async () => {
    if (!currentOrganization) {
      console.log('⚠️ No organization selected, skipping quotes fetch');
      return;
    }

    try {
      setLoading(true);
      console.log('📋 Fetching quotes for organization:', currentOrganization.name);
      
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching quotes:', error);
        return;
      }

      if (data) {
        // Transform Supabase data to Quote format
        const transformedQuotes: Quote[] = data.map(quote => ({
          id: quote.id,
          title: quote.title,
          description: quote.description || '',
          client: {
            name: quote.client_name || '',
            address: quote.client_address || '',
            phone: quote.client_phone,
            email: quote.client_email
          },
          status: quote.status,
          createdAt: quote.created_at,
          validUntil: quote.valid_until,
          sentDate: quote.sent_date,
          acceptedDate: quote.accepted_date,
          rejectedDate: quote.rejected_date,
          projectId: quote.project_id,
          materials: quote.materials || [],
          laborItems: quote.labor_items || [],
          fixedPrice: quote.fixed_price || 0,
          notes: quote.notes,
          taxRate: quote.tax_rate || settings.defaultTaxRate,
          discountRate: quote.discount_rate || settings.defaultDiscountRate || 0,
          taxAmount: quote.tax_amount || 0,
          discountAmount: quote.discount_amount || 0,
          subtotal: quote.subtotal || 0,
          total: quote.total || 0
        }));

        setQuotes(transformedQuotes);
      }
    } catch (error) {
      console.error('Error in fetchQuotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const addQuote = async (quote: Omit<Quote, 'id'>): Promise<Quote> => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }

    try {
      // Calculate totals if not provided
      let totals = { subtotal: 0, taxAmount: 0, discountAmount: 0, total: 0 };
      
      if (!quote.subtotal || !quote.total) {
        totals = calculateQuoteTotal({
          ...quote,
          id: '',
          subtotal: 0,
          taxAmount: 0,
          discountAmount: 0,
          total: 0
        });
      }

      console.log('📝 Creating quote for organization:', currentOrganization.name);
      
      const { data, error } = await supabase
        .from('quotes')
        .insert({
          organization_id: currentOrganization.id,
          title: quote.title,
          description: quote.description,
          client_name: quote.client.name,
          client_address: quote.client.address,
          client_phone: quote.client.phone,
          client_email: quote.client.email,
          status: quote.status,
          valid_until: quote.validUntil,
          sent_date: quote.sentDate,
          accepted_date: quote.acceptedDate,
          rejected_date: quote.rejectedDate,
          project_id: quote.projectId,
          materials: quote.materials,
          labor_items: quote.laborItems,
          fixed_price: quote.fixedPrice,
          notes: quote.notes,
          tax_rate: quote.taxRate || settings.defaultTaxRate,
          discount_rate: quote.discountRate || settings.defaultDiscountRate || 0,
          tax_amount: quote.taxAmount || totals.taxAmount,
          discount_amount: quote.discountAmount || totals.discountAmount,
          subtotal: quote.subtotal || totals.subtotal,
          total: quote.total || totals.total
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding quote:', error);
        throw error;
      }

      if (!data) {
        throw new Error('No data returned from quote creation');
      }

      const newQuote: Quote = {
        id: data.id,
        title: data.title,
        description: data.description || '',
        client: {
          name: data.client_name || '',
          address: data.client_address || '',
          phone: data.client_phone,
          email: data.client_email
        },
        status: data.status,
        createdAt: data.created_at,
        validUntil: data.valid_until,
        sentDate: data.sent_date,
        acceptedDate: data.accepted_date,
        rejectedDate: data.rejected_date,
        projectId: data.project_id,
        materials: data.materials || [],
        laborItems: data.labor_items || [],
        fixedPrice: data.fixed_price || 0,
        notes: data.notes,
        taxRate: data.tax_rate || settings.defaultTaxRate,
        discountRate: data.discount_rate || settings.defaultDiscountRate || 0,
        taxAmount: data.tax_amount || 0,
        discountAmount: data.discount_amount || 0,
        subtotal: data.subtotal || 0,
        total: data.total || 0
      };
      setQuotes(prev => [...prev, newQuote]);
      return newQuote;
    } catch (error) {
      console.error('Error in addQuote:', error);
      throw error;
    }
  };

  const updateQuote = async (updatedQuote: Quote) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .update({
          title: updatedQuote.title,
          description: updatedQuote.description,
          client_name: updatedQuote.client.name,
          client_address: updatedQuote.client.address,
          client_phone: updatedQuote.client.phone,
          client_email: updatedQuote.client.email,
          status: updatedQuote.status,
          valid_until: updatedQuote.validUntil,
          sent_date: updatedQuote.sentDate,
          accepted_date: updatedQuote.acceptedDate,
          rejected_date: updatedQuote.rejectedDate,
          project_id: updatedQuote.projectId,
          materials: updatedQuote.materials,
          labor_items: updatedQuote.laborItems,
          fixed_price: updatedQuote.fixedPrice,
          notes: updatedQuote.notes,
          tax_rate: updatedQuote.taxRate,
          discount_rate: updatedQuote.discountRate,
          tax_amount: updatedQuote.taxAmount,
          discount_amount: updatedQuote.discountAmount,
          subtotal: updatedQuote.subtotal,
          total: updatedQuote.total
        })
        .eq('id', updatedQuote.id);

      if (error) {
        console.error('Error updating quote:', error);
        throw error;
      }

      setQuotes(prev => 
        prev.map(quote => 
          quote.id === updatedQuote.id ? updatedQuote : quote
        )
      );
    } catch (error) {
      console.error('Error in updateQuote:', error);
      throw error;
    }
  };

  const deleteQuote = async (id: string) => {
    try {
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting quote:', error);
        throw error;
      }

      setQuotes(prev => prev.filter(quote => quote.id !== id));
    } catch (error) {
      console.error('Error in deleteQuote:', error);
      throw error;
    }
  };

  const getQuoteById = (id: string) => {
    return quotes.find(quote => quote.id === id);
  };

  const convertQuoteToProject = (quoteId: string) => {
    // This will be handled by the component that calls this function
    // It will create a project and update the quote status
  };

  const sendQuoteEmail = async (quoteId: string, recipientEmail: string, message?: string, pdfBase64?: string, pdfFilename?: string) => {
    console.log('📧 QuotesContext.sendQuoteEmail called with:', { quoteId, recipientEmail, message, hasPDF: !!pdfBase64 });
    try {
      console.log('📤 Invoking send-quote-email function...');
      const { data, error } = await supabase.functions.invoke('send-quote-email', {
        body: {
          quoteId,
          recipientEmail,
          message,
          pdfBase64,
          pdfFilename: pdfFilename || `quote-${quoteId}.pdf`
        }
      });

      console.log('📥 Supabase function response:', { data, error });

      if (error) {
        console.error('Error sending quote email:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in sendQuoteEmail:', error);
      throw error;
    }
  };

  return (
    <QuotesContext.Provider value={{
      quotes,
      loading,
      addQuote,
      updateQuote,
      deleteQuote,
      getQuoteById,
      convertQuoteToProject,
      fetchQuotes,
      sendQuoteEmail
    }}>
      {children}
    </QuotesContext.Provider>
  );
};